/**
 * Assistente AI (V58–V59): contratti della conversazione e catalogo delle
 * viste che l'assistente puo indicare.
 *
 * Nella prima versione l'assistente legge e basta. Quando gli si chiede di
 * scrivere non propone un'azione da confermare: indica la vista in cui farlo,
 * dove valgono le anteprime e le verifiche dell'interfaccia. Il catalogo qui
 * sotto e l'elenco di quelle destinazioni, con il permesso che le apre: un
 * link che la guardia negherebbe non arriva mai all'utente.
 */

import { z } from 'zod';
import { idSchema } from './contratti.js';
import type { Assegnazione, Permesso } from './permessi.js';
import { ambitoDelPermesso, puo } from './permessi.js';

/** Lunghezza massima di un messaggio scritto all'assistente. */
export const LUNGHEZZA_MESSAGGIO = 2000;

/** Giri di funzioni per risposta: oltre, l'assistente si ferma e lo dice. */
export const MAX_GIRI_FUNZIONI = 12;

/** Messaggi al mese per societa, finche il Super Admin non concede una deroga. */
export const MESSAGGI_ASSISTENTE_PREDEFINITI = 1000;

/** Conversazioni per pagina nello storico (V59), come gli altri elenchi paginati. */
export const RIGHE_CONVERSAZIONI = 10;

/**
 * Dopo quanti mesi di inattivita una conversazione si cancella da sola.
 * Si guarda l'ultimo messaggio, non la data di apertura: una conversazione
 * ripresa a settembre non sparisce perche era nata a febbraio.
 */
export const MESI_CONSERVAZIONE_CONVERSAZIONI = 6;

export interface VistaAssistente {
  titolo: string;
  /** Quando indicarla: il modello la legge per scegliere. */
  descrizione: string;
  ambito: 'squadra' | 'societa';
  permesso: Permesso;
  /**
   * Con `:ss` per le viste di squadra e `:id` per quelle di un elemento;
   * l'esecuzione di un test porta anche `:esecuzioneId`, dopo l'id del test.
   */
  percorso: string;
  /**
   * Di che cosa e l'id delle viste di un elemento, detto al modello nel
   * contesto: «dell'esecuzione» e non del test, che sta anche lui nel percorso.
   */
  elemento?: string;
}

export const VISTE_ASSISTENTE = {
  cruscotto: {
    titolo: 'Home',
    descrizione:
      'La pagina d’ingresso della squadra: partita sul campo, numeri della stagione (bilancio, ' +
      'gol, forma, disponibili, presenza agli allenamenti), agenda dei sette giorni, ultimi ' +
      'risultati, avvisi, migliori marcatori.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss',
  },
  rosa: {
    titolo: 'Rosa',
    descrizione:
      'Giocatori tesserati in quattro riquadri per reparto (portieri, difensori, centrocampisti, ' +
      'attaccanti), con numero, stato, diffide (badge giallo) e squalifiche (badge rosso) con la ' +
      'competizione, overall (media delle medie di famiglia), media degli attributi fisici, tecnici, mentali e portiere (le stesse per tutti i ruoli), ' +
      'minuti e voto medio; la tendina «Ordina per» le ordina tutte insieme per cognome (in ' +
      'partenza), numero di maglia, overall, minuti o voto medio. Da qui si aggiunge un tesseramento, ' +
      'si cambia il numero di maglia e si chiude un tesseramento; l’import rosa è una vista a parte.',
    ambito: 'squadra',
    permesso: 'giocatore.leggi',
    percorso: '/sq/:ss/rosa',
  },
  'import-rosa': {
    titolo: 'Import rosa',
    descrizione:
      "Copia la rosa da un'altra squadra-stagione: scegli l’origine, deseleziona chi non deve " +
      'entrare, conferma.',
    ambito: 'squadra',
    permesso: 'tesseramento.gestisci',
    percorso: '/sq/:ss/rosa/import',
  },
  allenamenti: {
    titolo: 'Allenamenti',
    descrizione:
      'Calendario ed elenco degli allenamenti. Da qui «Nuovo» apre il modale per programmarne ' +
      'uno solo (data, ora, luogo, note), anche dal giorno scelto nel calendario, e «In blocco» ' +
      'quello per gli allenamenti ricorrenti: giorni della settimana, ora, periodo, con ' +
      'l’anteprima delle date e delle sovrapposizioni con le partite, da cui si tolgono quelle ' +
      'da saltare prima di confermare.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/allenamenti',
  },
  'scheda-allenamento': {
    titolo: 'Scheda allenamento',
    descrizione:
      'Un allenamento, scheda Riepilogo: note, conteggi delle presenze e piano della seduta per ' +
      'gruppo; la presenza di ciascuno sta nell’Appello. Le altre schede (Componi, Appello, ' +
      'Registro seduta, Voti) stanno accanto nella stessa pagina; modifica, cambio di stato ed ' +
      'eliminazione sono i comandi a icona in testata, in ogni scheda.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/allenamenti/:id',
    elemento: 'dell’allenamento',
  },
  'composizione-seduta': {
    titolo: 'Composizione seduta',
    descrizione:
      'Scheda Componi dell’allenamento, divisa in Blocchi (si apre lì) e Gruppi. Gruppi: quattro di sistema ' +
      'per ruolo più eventuali custom, ciascuno con i suoi giocatori e la loro situazione (stato, ' +
      'minuti, voto, medie); il pulsante a destra della riga sposta il giocatore in un altro ' +
      'gruppo. Blocchi: schede o test della libreria assegnati a uno o più gruppi; RPE e minuti si ' +
      "fissano solo all'inserimento, dopo il blocco si elimina. Un blocco da test divide il " +
      'carico in parti uguali fra gli attributi delle misure e, dopo Chiudi appello, dal suo ' +
      'menu «⋯» «Risultati» apre l’esecuzione del test per i presenti. Per ogni gruppo, blocchi, ' +
      'minuti e dose target in UA (RPE × minuti) sommati.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/allenamenti/:id/blocchi',
    elemento: 'dell’allenamento',
  },
  'log-seduta': {
    titolo: 'Registro seduta',
    descrizione:
      'Minuti e RPE eseguiti da ogni presente in ogni blocco dei suoi gruppi, un gruppo alla ' +
      'volta. Le righe nascono ' +
      'chiudendo l’appello, allineate allo snapshot; In linea, Sotto e Sopra confrontano le UA. ' +
      'Il motivo compare solo se minuti o RPE differiscono dallo snapshot.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/allenamenti/:id/registro',
    elemento: 'dell’allenamento',
  },
  appello: {
    titolo: 'Appello',
    descrizione:
      'Presenze di un allenamento, solo il giorno della seduta: tre stati per giocatore — ' +
      'presente, assente, infortunato — o «tutti presenti». Alla prima apertura utile i noti ' +
      'infortunati e gli altri presenti sono già segnati. Chiudi appello blocca gruppi e presenze, ' +
      'segna svolto e compila il Registro seduta.',
    ambito: 'squadra',
    permesso: 'presenza.scrivi',
    percorso: '/sq/:ss/allenamenti/:id/presenze',
    elemento: 'dell’allenamento',
  },
  'voti-allenamento': {
    titolo: 'Voti allenamento',
    descrizione:
      'Voti dei giocatori in un allenamento svolto, per cognome con presenza, anno e ruolo: ' +
      'selettore da 1 a 10 a mezzi punti per riga, «—» toglie il voto.',
    ambito: 'squadra',
    permesso: 'voto.leggi',
    percorso: '/sq/:ss/allenamenti/:id/voti',
    elemento: 'dell’allenamento',
  },
  schede: {
    titolo: 'Libreria schede',
    descrizione:
      'Schede della società, in una card per tipo (esercizio, poi partita): RPE e minuti, ' +
      'varianti e mix a somma 100 riassunto per famiglia; ricerca per nome. Una scheda la ' +
      'svolge chiunque: nel mix entra qualunque attributo. ' +
      '«Nuova» apre il modale di creazione. La libreria è della società: la squadra ' +
      'nell’URL è solo il contesto.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/schede',
  },
  'dettaglio-scheda': {
    titolo: 'Scheda',
    descrizione:
      'Una scheda di libreria: mix per famiglia con la quota di ogni attributo, varianti, ' +
      'disattivazione. Modifica e nuova variante (solo dalla base) si aprono in un modale, ' +
      'con le quote a cursori a passi di 5; una scheda con varianti non si elimina.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/schede/:id',
    elemento: 'della scheda',
  },
  carichi: {
    titolo: 'Carichi',
    descrizione:
      'In cima la dose della rosa nel periodo e l’ACWR di squadra, con l’avviso di quanti ' +
      'sono sotto target; poi i totali per attributo con il filtro per categoria, che vale ' +
      'anche per la dose in cima e per il grafico nel tempo ' +
      '(barre della squadra, linee dei giocatori) e le card dei giocatori per ruolo, con ' +
      'accanto al titolo quanti sono in ciascuna lettura dell’ACWR. ' +
      'Si apre la scheda Carichi del giocatore. I totali contano tutti i log; il dettaglio ' +
      'solo le squadre dell’utente.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/carichi',
  },
  partite: {
    titolo: 'Partite',
    descrizione:
      'Calendario ed elenco delle partite, con filtri per stato e competizione. Da qui «Nuova» ' +
      'apre il modale per crearne una (data e ora, avversario, casa o trasferta, luogo, ' +
      'competizione anche nuova al volo, formato di gioco precompilato, note), anche dal giorno ' +
      'scelto nel calendario; salvata, si apre la sua scheda, dove si scelgono i convocati.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/partite',
  },
  'scheda-partita': {
    titolo: 'Scheda partita',
    descrizione:
      'Una partita: cambia forma con lo stato. Da programmata e da annullata competizione, ' +
      'luogo, formato e note stanno nel dialog aperto dal comando Informazioni in testata; da ' +
      'conclusa nella scheda Info. Da programmata: in ' +
      'testata anche i comandi a icona (modifica, che apre il modale con i dati della ' +
      'partita; a data passata «Giocata senza timer», che la chiude e la si ricostruisce ' +
      'dall’elenco eventi; annulla). Sotto, una card per reparto, una sotto l’altra, con ' +
      'convocati e titolari accanto al titolo, in ordine di cognome o per overall: righe della ' +
      'rosa (stati, anno e piede, minuti, voto, medie e overall) con la spunta che convoca e ' +
      'sotto l’interruttore che mette titolare. Nella barra sopra, sempre in vista, «Convoca ' +
      'tutti», i conteggi, l’interruttore «Overall» che ordina le card per overall, un ' +
      'avviso se tra i titolari non c’è un portiere o ce n’è più d’uno e ' +
      'i comandi a icona per salvare solo i convocati o confermare la formazione quando i ' +
      'titolari sono al completo. ' +
      'Live Timer da formazione a fine gara: in campo righe divise per reparto (portiere, ' +
      'difensori, centrocampisti, attaccanti) e in ordine di cognome, in panchina con il ruolo ' +
      'sotto il nome; ogni riga con gol e cartellini della partita, i minuti giocati ' +
      '(recuperi compresi), l’icona del cambio, che chiede chi entra o chi esce, e toccando il nome (da tablet in su già sulla riga), gli eventi di ' +
      'gioco a coppie ' +
      '(passaggio, palla, tiro); gol, autogol, giallo e rosso stanno nella fascia in ' +
      'basso e chiedono il giocatore (l’autogol offre anche quello avversario, senza ' +
      'giocatore; il gol chiede poi l’assist, facoltativo), accanto a gol subito, eventi, sospendi e comando del ' +
      'periodo. Accanto a «In campo» un avviso se non c’è un portiere o ce n’è più d’uno, ' +
      'senza bloccare i cambi. Un periodo o una ripresa non partono con uno in meno se dalla ' +
      'panchina può entrare qualcuno. Da conclusa: il risultato in testata, con i comandi a ' +
      'icona per modificare (matita, dopo una conferma apre la modifica con le schede Eventi e ' +
      'Carico) e riaprire; sotto le schede Info (risultato con gol e cartellini al minuto, ' +
      'informazioni della partita, statistiche di squadra), Tabellino, Cronaca e Voti.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/partite/:id',
    elemento: 'della partita',
  },
  tabellino: {
    titolo: 'Tabellino',
    descrizione:
      'Scheda Tabellino della partita conclusa: la formazione con minuti giocati (recuperi ' +
      'compresi) ed eventi per giocatore, ordinabile per colonna (di partenza per cognome); ' +
      'accanto al nome entrate, uscite, gialli e rossi con il minuto.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/partite/:id/tabellino',
    elemento: 'della partita',
  },
  'cronaca-partita': {
    titolo: 'Cronaca',
    descrizione:
      'Scheda Cronaca della partita conclusa: gli eventi minuto per minuto in una tabella ' +
      'divisa per tempo, con giocatore (chi esce e chi entra nelle sostituzioni) e il ' +
      'punteggio sulle voci che lo cambiano.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/partite/:id/cronaca',
    elemento: 'della partita',
  },
  'eventi-partita': {
    titolo: 'Elenco eventi',
    descrizione:
      'Scheda Eventi della modifica di una partita conclusa (matita in testata, dopo la ' +
      'conferma): una tabella divisa per tempo, una riga per evento con minuto, giocatore ' +
      'e autore, cestino o ripristino a icona, interruttore per mostrare i cancellati. ' +
      '«Aggiungi evento» inserisce con il minuto, anche entrate e uscite (i titolari ' +
      'entrano al minuto 0 del 1° periodo). È l’unico modo di ' +
      'correggere minuti e partecipazioni, e di ricostruire una partita giocata senza timer. ' +
      'Ogni modifica ricalcola le partecipazioni.',
    ambito: 'squadra',
    permesso: 'timer.opera',
    percorso: '/sq/:ss/partite/:id/modifica',
    elemento: 'della partita',
  },
  'carico-partita': {
    titolo: 'Carico della partita',
    descrizione:
      'Scheda Carico della modifica di una partita conclusa: la scheda di tipo partita e, per ' +
      'chi ha giocato, minuti (dalle partecipazioni) e RPE; un avviso se i minuti registrati ' +
      'non coincidono più con quelli giocati. Salvando sopra un carico già scritto si sostituisce.',
    ambito: 'squadra',
    permesso: 'allenamento.gestisci',
    percorso: '/sq/:ss/partite/:id/modifica/carico',
    elemento: 'della partita',
  },
  'voti-partita': {
    titolo: 'Voti partita',
    descrizione:
      'Scheda Voti della partita conclusa: votati e media in alto, selettore da 1 a 10 a mezzi ' +
      'punti per chi ha partecipazioni, cioè un ingresso negli eventi, «—» toglie il voto.',
    ambito: 'squadra',
    permesso: 'voto.leggi',
    percorso: '/sq/:ss/partite/:id/voti',
    elemento: 'della partita',
  },
  'attributi-squadra': {
    titolo: 'Attributi della squadra',
    descrizione:
      'Attributi della rosa, una scheda per famiglia (fisici, tecnici, mentali, portiere): ' +
      'ultimo valore di ogni attributo per giocatore, da 1 a 100, con la media della famiglia; ' +
      'ogni scheda elenca tutta la rosa, qualunque ruolo (il portiere ha i suoi tecnici, il ' +
      'terzino le sue parate). Si ordina per numero, cognome, media o un attributo. «Nuova sessione» (Mister e ' +
      'Dirigente) rende modificabili le celle di chi è in rosa, con una data unica anche fra ' +
      'più famiglie: ogni valore è una nuova misurazione, non sovrascrive le precedenti. ' +
      'Per chi è il più veloce o il migliore in un attributo è questa la vista, non il confronto.',
    ambito: 'squadra',
    permesso: 'misurazione.leggi',
    percorso: '/sq/:ss/valutazioni/attributi',
  },
  confronto: {
    titolo: 'Confronto giocatori',
    descrizione:
      'Confronta i giocatori scelti (anche uno solo; senza scelta i portieri) dalla tendina ' +
      'con ricerca dei Carichi. Attributi: radar degli ultimi valori sui soli attributi che ' +
      'tutti hanno rilevato (tabella con meno di tre), filtro per categoria, e con lo switch ' +
      '«Andamento nel tempo» le linee di un attributo scelto. Partite concluse, filtrabili per ' +
      'competizione e periodo (mese, trimestre, stagione o intervallo): barre degli eventi ' +
      'scelti (presenze di default), dei minuti e della media voto, o con lo switch le linee ' +
      'partita per partita, con le presenze sommate nel tempo e il voto di ogni partita. ' +
      'Le scale vanno dal valore più basso al più alto mostrato. ' +
      'Non è la classifica di un attributo su tutta la rosa.',
    ambito: 'squadra',
    permesso: 'misurazione.leggi',
    percorso: '/sq/:ss/valutazioni/confronto',
  },
  statistiche: {
    titolo: 'Statistiche squadra',
    descrizione:
      'Scheda «Squadra» delle statistiche, filtrabile per competizione: partite giocate, vinte, ' +
      'pareggiate e perse, minuti di squadra e voto medio; gol fatti e subiti con media a ' +
      'partita, differenza reti, assist e autogol; ogni evento con totale e media a partita, ' +
      'e per tiri, passaggi e palle il confronto con la precisione in percentuale; cartellini; ' +
      'allenamenti svolti e partecipazione della stagione; gol per periodo.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/statistiche',
  },
  classifiche: {
    titolo: 'Classifiche giocatori',
    descrizione:
      'Scheda «Giocatori» delle statistiche, filtrabile per competizione: i primi cinque per ' +
      'gol, assist, minuti e media voto, poi la classifica di tutti con presenze, minuti, ' +
      'gol, assist, tiri, passaggi, palle, cartellini e media voto, ordinabile con una tendina.',
    ambito: 'squadra',
    permesso: 'partita.leggi',
    percorso: '/sq/:ss/statistiche/giocatori',
  },
  'statistiche-presenze': {
    titolo: 'Statistiche presenze',
    descrizione:
      'Scheda «Presenze» delle statistiche: allenamenti svolti e annullati, partecipazione ' +
      'della rosa, e per reparto ogni giocatore con presenze, assenze, infortuni, non ' +
      'rilevati e percentuale, ordinabili con una tendina.',
    ambito: 'squadra',
    permesso: 'allenamento.leggi',
    percorso: '/sq/:ss/statistiche/presenze',
  },
  impostazioni: {
    titolo: 'Impostazioni squadra',
    descrizione:
      'Categoria, campionato, girone, formato di gioco e tipi di evento attivi del Live Timer; ' +
      'salva. Il formato è copiato sulle partite alla creazione: modificarlo qui non tocca quelle ' +
      'già create.',
    ambito: 'squadra',
    permesso: 'societa.squadre.gestisci',
    percorso: '/sq/:ss/impostazioni',
  },
  staff: {
    titolo: 'Staff della squadra',
    descrizione:
      'Mister e Dirigenti assegnati alla squadra: assegna un account esistente o revoca.',
    ambito: 'squadra',
    permesso: 'societa.utenze.gestisci',
    percorso: '/sq/:ss/staff',
  },
  test: {
    titolo: 'Test di misura',
    descrizione:
      'Libreria dei test di misura della società: esercizi codificati (sprint, salto, navetta…) ' +
      'il cui risultato grezzo diventa un punteggio da 1 a 100 per gli attributi che valutano. ' +
      'Una card con tutti i test; ogni riga ha le misure se sono più d’una, metodo, RPE e minuti ' +
      'se ci sono, esecuzioni e attributi. Un test lo svolge chiunque, qualunque attributo ' +
      'valuti. Filtri per nome, metodo di calcolo (di almeno una misura) e stato. «Nuovo» apre ' +
      'il modale di creazione (nome, istruzioni, RPE e minuti di riferimento, e le misure, ' +
      'ognuna con nome, unità, attributi, metodo e parametri e «Prova il calcolo»); ' +
      'il pulsante ▶ di una riga lo esegue. ' +
      'La libreria è della società: la squadra nell’URL è solo il contesto.',
    ambito: 'squadra',
    permesso: 'misurazione.leggi',
    percorso: '/sq/:ss/test',
  },
  'dettaglio-test': {
    titolo: 'Test',
    descrizione:
      'Un test di misura, a card: la regola di calcolo in una frase con i parametri e gli ' +
      'attributi per famiglia (una card per misura se sono più d’una), le istruzioni e le ' +
      'esecuzioni della squadra aperta; sotto il titolo metodo, unità, RPE e minuti. Da qui si ' +
      'esegue, si modifica in un modale, si disattiva o ' +
      'riattiva, si elimina (solo senza esecuzioni) e si apre un’esecuzione.',
    ambito: 'squadra',
    permesso: 'misurazione.leggi',
    percorso: '/sq/:ss/test/:id',
    elemento: 'del test',
  },
  'esegui-test': {
    titolo: 'Esecuzione test',
    descrizione:
      'Registra un test svolto: data, giocatori della rosa (portieri compresi), per ogni riga un ' +
      'risultato per misura con il punteggio calcolato mentre si scrive (una misura vuota non ' +
      'si salva); salvando nascono le misurazioni degli attributi di ogni misura. Si apre con ' +
      'l’id del test.',
    ambito: 'squadra',
    permesso: 'misurazione.scrivi',
    percorso: '/sq/:ss/test/:id/esegui',
    elemento: 'del test',
  },
  'esecuzione-test': {
    titolo: 'Esecuzione test',
    descrizione:
      'Un’esecuzione già salvata: risultati e punteggi di ogni giocatore, misura per misura, ' +
      'con le misure di allora. Qui si corregge un risultato (le misurazioni si riscrivono), si ' +
      'svuota una misura, si toglie un giocatore o si elimina l’esecuzione: le misurazioni nate da un test si correggono solo ' +
      'da qui. L’Admin la apre in sola lettura. Chiede l’id del test e quello dell’esecuzione.',
    ambito: 'squadra',
    permesso: 'misurazione.leggi',
    percorso: '/sq/:ss/test/:id/esecuzioni/:esecuzioneId',
    elemento: 'dell’esecuzione',
  },
  giocatori: {
    titolo: 'Giocatori',
    descrizione:
      'Anagrafiche dei giocatori della società; da qui l’Admin crea una nuova anagrafica e archivia.',
    ambito: 'societa',
    permesso: 'giocatore.leggi',
    percorso: '/giocatori',
  },
  'nuovo-giocatore': {
    titolo: 'Nuovo giocatore',
    descrizione:
      "Crea l'anagrafica di un giocatore, con ruolo obbligatorio e piede, e tesseramento " +
      'immediato facoltativo a una squadra-stagione.',
    ambito: 'societa',
    permesso: 'giocatore.gestisci',
    percorso: '/giocatori/nuovo',
  },
  'scheda-giocatore': {
    titolo: 'Scheda giocatore',
    descrizione:
      'Un giocatore: in testata, accanto al nome, overall e medie di famiglia come nella rosa (a ' +
      'chi legge le misurazioni); anagrafica (la modifica l’Admin); ruolo, piede, stato (disponibile, ' +
      'affaticato, recupero, infortunato), diffida o squalifica per competizione e note (li ' +
      'cambiano anche Mister e Dirigente delle sue squadre); storico dei tesseramenti (solo Admin), carichi, ' +
      'attributi, voti e statistiche, e il trend nel tempo sulle stagioni scelte: un grafico per ' +
      'i voti di partite e allenamenti, uno per gli eventi scelti con i minuti in barre (gli ' +
      'interruttori Minuti e Progressivo), uno per gli attributi scelti dalla tendina (una ' +
      'famiglia li sceglie tutti). Non confronta con i compagni: per quello c’è il Confronto.',
    ambito: 'societa',
    permesso: 'giocatore.leggi',
    percorso: '/giocatori/:id',
    elemento: 'del giocatore',
  },
  'carichi-giocatore': {
    titolo: 'Carichi del giocatore',
    descrizione:
      'Dose e ACWR di un giocatore in cima, grafici della dose per attributo e nel tempo ' +
      '(solo i periodi con un carico); il filtro per categoria vale per la dose in cima, ' +
      'per le barre e per la linea. Poi gli attributi con carico divisi in fisici, tecnici, ' +
      'mentali e portiere, ognuno richiudibile con il suo totale. ' +
      'Visibile anche al Dirigente, senza voti.',
    ambito: 'societa',
    permesso: 'allenamento.leggi',
    percorso: '/giocatori/:id/carichi',
    elemento: 'del giocatore',
  },
  competizioni: {
    titolo: 'Competizioni',
    descrizione: 'Crea, rinomina, disattiva le competizioni.',
    ambito: 'societa',
    permesso: 'competizione.gestisci',
    percorso: '/competizioni',
  },
  stagioni: {
    titolo: 'Stagioni',
    descrizione: 'Stagioni della società: crea, modifica, imposta come corrente.',
    ambito: 'societa',
    permesso: 'societa.stagioni.gestisci',
    percorso: '/amministrazione/stagioni',
  },
  squadre: {
    titolo: 'Squadre',
    descrizione: 'Squadre della società: crea, rinomina, disattiva.',
    ambito: 'societa',
    permesso: 'societa.squadre.gestisci',
    percorso: '/amministrazione/squadre',
  },
  'squadre-stagione': {
    titolo: 'Squadre-stagione',
    descrizione:
      'Quali squadre giocano in quale stagione: crea l’incrocio dalla matrice, apri le sue ' +
      'impostazioni o l’import rosa.',
    ambito: 'societa',
    permesso: 'societa.squadre.gestisci',
    percorso: '/amministrazione/squadre-stagione',
  },
  account: {
    titolo: 'Account e ruoli',
    descrizione:
      'Account della società: invita, disattiva, riattiva, modifica ruoli, reinvia o annulla un ' +
      'invito in attesa.',
    ambito: 'societa',
    permesso: 'societa.utenze.gestisci',
    percorso: '/amministrazione/account',
  },
  'catalogo-attributi': {
    titolo: 'Catalogo attributi',
    descrizione: 'Attributi dei giocatori, valutati da 1 a 100: crea, modifica, disattiva.',
    ambito: 'societa',
    permesso: 'attributo.catalogo.gestisci',
    percorso: '/amministrazione/attributi',
  },
} as const satisfies Record<string, VistaAssistente>;

export type ChiaveVista = keyof typeof VISTE_ASSISTENTE;

export const CHIAVI_VISTA = Object.keys(VISTE_ASSISTENTE) as [ChiaveVista, ...ChiaveVista[]];

/** La vista aperta quando si scrive all'assistente, con l'elemento se ne ha uno. */
export const vistaApertaSchema = z.object({
  chiave: z.enum(CHIAVI_VISTA),
  id: idSchema.optional(),
});

export type VistaAperta = z.infer<typeof vistaApertaSchema>;

export const messaggioAssistenteSchema = z.object({
  /** Assente per iniziare una conversazione nuova. */
  conversazioneId: idSchema.optional(),
  testo: z
    .string()
    .trim()
    .min(1, 'Scrivi una domanda.')
    .max(LUNGHEZZA_MESSAGGIO, `Massimo ${LUNGHEZZA_MESSAGGIO} caratteri.`),
  squadraStagioneId: idSchema.optional(),
  vista: vistaApertaSchema.optional(),
});

export type MessaggioAssistente = z.infer<typeof messaggioAssistenteSchema>;

/** Le finestre del filtro per periodo dello storico, contate all'indietro da oggi. */
export const PERIODI_CONVERSAZIONI = ['settimana', 'mese', 'trimestre'] as const;
export type PeriodoConversazioni = (typeof PERIODI_CONVERSAZIONI)[number];

export const GIORNI_PERIODO_CONVERSAZIONI: Record<PeriodoConversazioni, number> = {
  settimana: 7,
  mese: 30,
  trimestre: 90,
};

/** Il filtro per squadra delle conversazioni nate fuori da una squadra. */
export const SENZA_SQUADRA = 'nessuna';

const facoltativo = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .union([z.literal(''), schema])
    .optional()
    .transform((v) => (v === '' ? undefined : (v as z.infer<T> | undefined)));

/**
 * Filtri e pagina dello storico, dalla query string: cerca nel titolo, la
 * squadra in cui la conversazione e nata (o nessuna), l'ultimo messaggio
 * dentro la finestra scelta. La pagina conta da 1.
 */
export const filtriConversazioniSchema = z.object({
  q: facoltativo(z.string().trim().max(100)),
  squadra: facoltativo(z.union([z.literal(SENZA_SQUADRA), z.string().uuid('Squadra non valida.')])),
  periodo: facoltativo(z.enum(PERIODI_CONVERSAZIONI)),
  pagina: z.coerce.number().int('Pagina non valida.').min(1, 'Pagina non valida.').default(1),
});

export type FiltriConversazioni = z.output<typeof filtriConversazioniSchema>;

/**
 * Percorso di una vista per l'utente, o null se la guardia la negherebbe o
 * manca un parametro. Le viste di squadra si verificano sulla squadra, quelle
 * di societa su almeno una squadra o su tutta la societa, come il menu.
 */
export function percorsoVista(
  assegnazioni: readonly Assegnazione[],
  chiave: ChiaveVista,
  parametri: { squadraStagioneId?: string; id?: string; esecuzioneId?: string },
): string | null {
  const vista: VistaAssistente = VISTE_ASSISTENTE[chiave];
  if (vista.ambito === 'squadra') {
    if (!parametri.squadraStagioneId) return null;
    if (!puo(assegnazioni, vista.permesso, parametri.squadraStagioneId)) return null;
  } else {
    const ambito = ambitoDelPermesso(assegnazioni, vista.permesso);
    if (ambito !== 'societa' && ambito.length === 0) return null;
  }
  if (vista.percorso.includes(':id') && !parametri.id) return null;
  if (vista.percorso.includes(':esecuzioneId') && !parametri.esecuzioneId) return null;
  return vista.percorso
    .replace(':ss', parametri.squadraStagioneId ?? '')
    .replace(':esecuzioneId', parametri.esecuzioneId ?? '')
    .replace(':id', parametri.id ?? '');
}

const UUID = '[0-9a-fA-F-]{36}';

/**
 * Il contesto che il pannello manda con la domanda, ricavato dal percorso
 * aperto: la squadra-stagione e, se il percorso e una vista del catalogo,
 * quale, con l'id dell'elemento. Vince la vista piu specifica, perche
 * `/sq/x/partite` e prefisso di `/sq/x/partite/:id/tabellino`.
 *
 * L'elemento e l'ultimo id del percorso: per l'esecuzione di un test,
 * l'esecuzione e non il test, che l'esecuzione porta con se.
 *
 * Un percorso che non e nel catalogo (per esempio una vista senza interesse
 * per l'assistente) lascia comunque la squadra-stagione.
 */
export function contestoDaPercorso(percorso: string): {
  squadraStagioneId?: string;
  vista?: VistaAperta;
} {
  const contesto: { squadraStagioneId?: string; vista?: VistaAperta } = {};
  const squadra = new RegExp(`^/sq/(${UUID})(/|$)`).exec(percorso);
  if (squadra) contesto.squadraStagioneId = squadra[1];

  const trovate = CHIAVI_VISTA.map((chiave) => {
    const vista: VistaAssistente = VISTE_ASSISTENTE[chiave];
    const schema = vista.percorso
      .replace(':ss', UUID)
      .replace(':esecuzioneId', `(${UUID})`)
      .replace(':id', `(${UUID})`);
    const trovato = new RegExp(`^${schema}$`).exec(percorso);
    if (!trovato) return null;
    // L'indice 0 e il percorso intero: l'elemento e l'ultimo gruppo, se ce n'e uno.
    const id = trovato.length > 1 ? trovato.at(-1) : undefined;
    return { chiave, id, lunghezza: vista.percorso.length };
  }).filter((v) => v !== null);

  const migliore = trovate.sort((a, b) => b.lunghezza - a.lunghezza)[0];
  if (migliore) {
    contesto.vista = { chiave: migliore.chiave, ...(migliore.id && { id: migliore.id }) };
  }
  return contesto;
}
